import { closestCenter, DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import * as Sentry from "@sentry/react";
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { default as InlineSVG, default as SVG } from "react-inlinesvg";
import { useImmer } from "use-immer";
import { formatCharacterRoleLabel, getCharacterTemplateEntityType, slugifyCharacterRole, stripCharacterTemplatePrefix } from "../../../../src/shared/characterTemplates.js";
import {
  DEFAULT_CHARACTER_CUSTOM_FIELDS,
  DEFAULT_CHARACTER_ROLE_TEMPLATES,
  generateId,
  MAX_NUM_CHARACTERS_PER_FIELD_LABEL,
  MAX_NUM_CUSTOM_FIELDS,
} from "../../../../src/shared/schemas/CharacterSchema";
import { DEFAULT_ROLES } from "../../../../src/shared/schemas/WorldbuildingEntrySchema";
import { AppEventPublisher, StoryBibleAction, StoryBibleType } from "../../AppEventPublisher.js";
import Auth from "../../Auth";
import IconButton from "../IconButton";
import GeneralBaseModal from "../Modals/GeneralBaseModal";
import StyledSelect from "../StyledSelect";
import { ReactSelectFocusManagerContext } from "../StyledSelect/ReactSelectFocusManager";
import TextButton from "../TextButton";
import { useToast } from "../ToastWrapper";
import { CustomFooterStyles, CustomizeEntityDefaultsContainer } from "./styles";
import UnsavedChangesConfirm from "./UnsavedChangesConfirm";

const fieldsEqual = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if ((a[i]?.id ?? "") !== (b[i]?.id ?? "")) return false;
    if ((a[i]?.label ?? "") !== (b[i]?.label ?? "")) return false;
  }
  return true;
};

function SortableItem({ field, onFieldLabelChange, onDeleteField }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      className="trait-list-item"
      style={style}
      key={field.id}
      ref={setNodeRef}
    >
      <div
        className="grabber"
        {...attributes}
        {...listeners}
      >
        <InlineSVG src="/assets/grabber.svg" />
      </div>
      <input
        type="text"
        value={field.label}
        onChange={(e) => onFieldLabelChange(e, field.id)}
        placeholder="Name of Trait..."
      />
      <IconButton
        src="/assets/close.svg"
        onClick={(e) => onDeleteField(e, field.id)}
        ariaLabel="Remove field from defaults"
      />
    </li>
  );
}

const DEFAULT_CHARACTER_OPTION = DEFAULT_CHARACTER_ROLE_TEMPLATES[0];

export default function CustomizeEntityDefaults({
  singleEntityType,
  singleEntityTypeLabel,
  isOpen,
  setIsOpen,
  addingDefaultFields,
  updateCurrentEntitiesWithFields,
  updatedEntityNames,
  doneUpdating,
  onDefaultsChanged,
  isCustomizingWhileImporting,
  setIsCustomizingWhileImporting,
  setImportIsOpen,
}) {
  const { focusedElement, focusElement } = useContext(ReactSelectFocusManagerContext);
  const selectRef = useRef(null);
  const isCharacterMode = singleEntityType === "character";
  const [entityType, setEntityType] = useState(isCharacterMode ? getCharacterTemplateEntityType(DEFAULT_CHARACTER_OPTION.optionValue) : singleEntityType); // canonical for characters
  const [entityTypeLabel, setEntityTypeLabel] = useState(isCharacterMode ? DEFAULT_CHARACTER_OPTION.name : singleEntityTypeLabel);
  const [availableEntityTypes, setAvailableEntityTypes] = useState([]);
  const [customizedDefaultRoles, setCustomizedDefaultRoles] = useState([]);
  const [creatingNewCustomDefault, setCreatingNewCustomDefault] = useState(false);
  const [entityTypeOptions, setEntityTypeOptions] = useState([]);
  const [selectedEntityTypeOption, setSelectedEntityTypeOption] = useState(); // "character" or "location" or "item" or "faction" or "event" or "plot"
  const { setToast } = useToast();

  const [fields, setFields] = useImmer([]); // [ { id: string, label: string }, ... ]
  const [baselineFields, setBaselineFields] = useState([]); // snapshot of fields at last load/save — used for dirty detection
  const [currentDefaultsId, setCurrentDefaultsId] = useState("");
  const [saving, setSaving] = useState(false);
  const [addToCurrent, setAddToCurrent] = useState(false);
  const [pendingEntityChange, setPendingEntityChange] = useState(null);
  const pendingEntityChangeRef = useRef(null);
  pendingEntityChangeRef.current = pendingEntityChange;

  const loadFields = (newFields) => {
    setFields(() => newFields);
    setBaselineFields(newFields);
  };

  const isDirty = !fieldsEqual(fields, baselineFields);

  // Triggers state update so that even if a user selects the same option from the dropdown,
  // we can refocus the input within the dropdown
  const [menuIsClosed, setMenuIsClosed] = useState(false);

  const fetchDefaultFields = async () => {
    const encodedEntityType = encodeURIComponent(entityType);
    const result = await Auth.fetchWithIdToken(`/api/entity-default-templates/${encodedEntityType}`);
    const { defaultTemplate } = result;

    if (!defaultTemplate) {
      if (isCharacterMode || singleEntityType === "character") {
        const roleSlug = stripCharacterTemplatePrefix(entityType);
        const fallbackRole = DEFAULT_CHARACTER_ROLE_TEMPLATES.find((role) => role.optionValue === roleSlug);
        loadFields(fallbackRole?.defaultFields || DEFAULT_CHARACTER_CUSTOM_FIELDS);
      }
      setCurrentDefaultsId("");
      return;
    }

    setCurrentDefaultsId(defaultTemplate.id); // save the id of the current defaults

    const { defaultFields } = defaultTemplate;
    loadFields(defaultFields);
  };

  const loadAvailableDefaultTemplates = async () => {
    const result = await Auth.fetchWithIdToken("/api/entity-default-templates/list", isCharacterMode ? { entityTypePrefix: "character:" } : {});
    const { defaultTemplates } = result;
    setAvailableEntityTypes(defaultTemplates);
  };

  useLayoutEffect(() => {
    focusedElement.current = selectRef.current;
    focusElement();
  }, [availableEntityTypes, selectedEntityTypeOption, entityTypeLabel, menuIsClosed]);

  const handleMenuClose = () => {
    setMenuIsClosed(true);
  };

  const handleMenuOpen = () => {
    setMenuIsClosed(false);
  };

  useEffect(() => {
    if (singleEntityType && !isCharacterMode) return;
    loadAvailableDefaultTemplates();
  }, [isOpen, isCharacterMode]);

  useEffect(() => {
    if (singleEntityType) {
      // we handle this was a larger fetch when we load the available entity types
      fetchDefaultFields();
    }
  }, [entityType]);

  useEffect(() => {
    if (isCharacterMode) {
      const builtinOptions = DEFAULT_CHARACTER_ROLE_TEMPLATES.map((role) => ({
        value: getCharacterTemplateEntityType(role.optionValue),
        label: role.name,
      }));
      const builtinSlugSet = new Set(DEFAULT_CHARACTER_ROLE_TEMPLATES.map((role) => role.optionValue));
      const overrides = availableEntityTypes.filter(({ entityType }) => builtinSlugSet.has(stripCharacterTemplatePrefix(entityType)));
      const customOptions = availableEntityTypes
        .filter(({ entityType }) => !builtinSlugSet.has(stripCharacterTemplatePrefix(entityType)))
        .map(({ entityType }) => ({
          value: entityType,
          label: formatCharacterRoleLabel(stripCharacterTemplatePrefix(entityType)),
        }));
      setEntityTypeOptions([{ value: "sudo-custom", label: "Custom" }, ...builtinOptions, ...customOptions]);
      setCustomizedDefaultRoles(overrides);
      return;
    }
    if (singleEntityType) return;
    const defaultRoleOptions = DEFAULT_ROLES.map((role) => ({ value: role.optionValue, label: role.name }));
    // for the available options, we want to exclude repeating default roles
    // and characters are handled separately.
    const nonCharacterTypes = availableEntityTypes.filter(({ entityType }) => !entityType.startsWith("character"));
    const withoutDefaultRoles = nonCharacterTypes.filter(({ entityType }) => !DEFAULT_ROLES.find((role) => role.optionValue === entityType));
    const userOptions = withoutDefaultRoles.map(({ entityType }) => ({ value: entityType, label: entityType }));
    const customizedDefaultRoles = nonCharacterTypes.filter(({ entityType }) => DEFAULT_ROLES.find((role) => role.optionValue === entityType));
    setEntityTypeOptions([...defaultRoleOptions, ...userOptions]);
    setCustomizedDefaultRoles(customizedDefaultRoles);
  }, [availableEntityTypes, isCharacterMode, singleEntityType]);

  useEffect(() => {
    if (!isCharacterMode || !entityTypeOptions.length) return;
    const matchingOption = entityTypeOptions.find((option) => option.value === entityType);
    if (matchingOption) {
      setSelectedEntityTypeOption(matchingOption);
      return;
    }
    if (!entityType) return;
    setSelectedEntityTypeOption({
      value: entityType,
      label: entityTypeLabel || formatCharacterRoleLabel(stripCharacterTemplatePrefix(entityType)),
    });
  }, [entityTypeOptions, entityType, entityTypeLabel, isCharacterMode]);

  useEffect(() => {
    if (isCharacterMode || !entityTypeOptions.length) return;
    if (selectedEntityTypeOption) {
      if (selectedEntityTypeOption.label) return;
      if (!selectedEntityTypeOption.label) {
        setSelectedEntityTypeOption(entityTypeOptions[0]);
      }
    } else {
      setSelectedEntityTypeOption(entityTypeOptions[0]);
    }
  }, [entityTypeOptions, isCharacterMode, selectedEntityTypeOption]);

  useEffect(() => {
    if (!selectedEntityTypeOption) return;
    setEntityType(selectedEntityTypeOption.value);
    setEntityTypeLabel(selectedEntityTypeOption.label);
    // only search custom templates here, when selecting default templates we don't need an existingOption
    const existingOption = availableEntityTypes.find(({ entityType }) => entityType === selectedEntityTypeOption.value);
    if (existingOption) {
      setCurrentDefaultsId(existingOption.id);
      loadFields(existingOption.defaultFields);
      return;
    }
    setCurrentDefaultsId("");

    if (isCharacterMode) {
      const roleSlug = stripCharacterTemplatePrefix(selectedEntityTypeOption.value);
      const defaultSelected = DEFAULT_CHARACTER_ROLE_TEMPLATES.find((role) => role.optionValue === roleSlug);
      if (defaultSelected) {
        loadFields(defaultSelected.defaultFields);
        return;
      }
    } else {
      const defaultSelected = DEFAULT_ROLES.find((role) => role.optionValue === selectedEntityTypeOption.value);
      if (defaultSelected) {
        loadFields(defaultSelected.defaultFields);
        return;
      }
    }

    // this is a completely new one. Just add description as a field.
    loadFields([{ id: generateId(10), label: "Description" }]);
  }, [selectedEntityTypeOption, availableEntityTypes, isCharacterMode]);

  // "Add to Current" is per-entity-type intent — clear it when the user picks a different type.
  useEffect(() => {
    setAddToCurrent(false);
  }, [selectedEntityTypeOption?.value]);

  const handleChangeFieldLabel = (e, id) => {
    e.preventDefault();
    const newLabel = e.target.value;

    if (newLabel.length > MAX_NUM_CHARACTERS_PER_FIELD_LABEL) {
      setToast(`Trait names must be less than ${MAX_NUM_CHARACTERS_PER_FIELD_LABEL} characters`);
      return;
    }

    setFields((draft) => {
      const field = draft.find((field) => field.id === id);
      if (!field) return;
      field.label = newLabel;
    });
  };

  const handleAddField = (e) => {
    e.preventDefault();
    if (fields.length >= MAX_NUM_CUSTOM_FIELDS) {
      setToast(`You can only have ${MAX_NUM_CUSTOM_FIELDS} ${entityTypeLabel} Traits`);
      return;
    }
    setFields((draft) => {
      draft.push({ id: generateId(10), label: "" });
    });
  };

  const handleDeleteField = (e, id) => {
    e.preventDefault();
    setFields((draft) => {
      return draft.filter((field) => field.id !== id);
    });
  };

  const selectValue =
    isCharacterMode && !selectedEntityTypeOption && entityType
      ? { value: entityType, label: entityTypeLabel || formatCharacterRoleLabel(stripCharacterTemplatePrefix(entityType)) }
      : selectedEntityTypeOption;
  const selectedOptionValue = selectValue?.value;
  const selectedIsCharacterBuiltin =
    isCharacterMode && selectedOptionValue && DEFAULT_CHARACTER_ROLE_TEMPLATES.some((role) => getCharacterTemplateEntityType(role.optionValue) === selectedOptionValue);
  const selectedHasOverride = !!selectedOptionValue && customizedDefaultRoles.some(({ entityType }) => entityType === selectedOptionValue);
  const shouldShowDelete =
    !!selectedOptionValue && !creatingNewCustomDefault && (isCharacterMode ? !selectedIsCharacterBuiltin : !DEFAULT_ROLES.some((role) => role.optionValue === selectedOptionValue));
  const shouldShowReset = !!selectedOptionValue && selectedHasOverride;
  const readableSelectedLabel = selectValue
    ? isCharacterMode
      ? selectValue.label || formatCharacterRoleLabel(stripCharacterTemplatePrefix(selectValue.value))
      : selectValue.value
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
    : "";

  const createCustomDefaults = async () => {
    const result = await Auth.fetchWithIdToken("/api/entity-default-templates/", {
      entityType,
      fieldNames: fields,
    });
    const eventType = singleEntityType === "character" ? StoryBibleType.characters : StoryBibleType.worldbuilding;
    AppEventPublisher.storyBible.action(eventType, StoryBibleAction.CreateCustomDefaults);
    if (result.error) {
      setToast(result.error);
      return result;
    }
    if (result.success && result.defaultTemplate?.id) {
      setCurrentDefaultsId(result.defaultTemplate.id);
    }
    return result;
  };

  const deleteCustomDefaults = async (options) => {
    try {
      const result = await Auth.fetchWithIdToken(`/api/entity-default-templates/${currentDefaultsId}`, {
        method: "DELETE",
      });

      if (result.error) {
        setToast(result.error);
      } else if (result.success) {
        await loadAvailableDefaultTemplates(); // Ensure this is awaited if it's asynchronous
        // Use a state update callback or effect to handle entityTypeOptions updates
        if (options.shouldReset) {
          setSelectedEntityTypeOption(entityTypeOptions[0]);
        }
        onDefaultsChanged();
      }
    } catch (error) {
      if (error.status === 404) {
        // User likely hasn't saved their changes, so just remove the custom trait category
        // from the list
        await loadAvailableDefaultTemplates(); // Ensure this is awaited if it's asynchronous
        // Use a state update callback or effect to handle entityTypeOptions updates
        if (options.shouldReset) {
          setSelectedEntityTypeOption(entityTypeOptions[0]);
        }
        onDefaultsChanged();
        return;
      }

      Sentry.captureMessage("Error deleting custom entity defaults", {
        extra: {
          error,
        },
      });
      setToast(error.message);
    }
  };

  const updateCustomDefaults = async () => {
    const result = await Auth.fetchWithIdToken(`/api/entity-default-templates/${currentDefaultsId}`, {
      method: "PUT",
      fieldNames: fields,
    });

    if (result.error) {
      setToast(result.error);
    }
    return result;
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setFields((draft) => {
        const oldIndex = draft.findIndex((field) => field.id === active.id);
        const newIndex = draft.findIndex((field) => field.id === over.id);
        const item = draft[oldIndex];

        draft.splice(oldIndex, 1);
        draft.splice(newIndex, 0, item);
      });
    }
  };

  const applyEntityTypeChange = (newValue) => {
    focusedElement.current = selectRef.current;

    if (isCharacterMode) {
      if (!newValue) return;
      if (newValue.value === "sudo-custom") {
        setSelectedEntityTypeOption(null);
        setCreatingNewCustomDefault(true);
        setEntityType("");
        setEntityTypeLabel("Custom Character Type");
        loadFields([{ id: generateId(10), label: "Description" }]);
        setCurrentDefaultsId("");
        return;
      }
      if (newValue.__isNew__) {
        const rawLabel = newValue.label || newValue.value;
        const slug = slugifyCharacterRole(rawLabel);
        const canonicalValue = getCharacterTemplateEntityType(slug);
        const label = rawLabel || formatCharacterRoleLabel(slug);
        const option = { value: canonicalValue, label };
        setEntityTypeOptions((options) => [...options, option]);
        setSelectedEntityTypeOption(option);
        setEntityType(canonicalValue);
        setEntityTypeLabel(label);
        setCreatingNewCustomDefault(true);
        loadFields([{ id: generateId(10), label: "Description" }]);
        setCurrentDefaultsId("");
        return;
      }
      setCreatingNewCustomDefault(false);
      setSelectedEntityTypeOption(newValue);
      return;
    }

    if (newValue.value === "sudo-custom") {
      setSelectedEntityTypeOption(null);
      return;
    }
    const isExistingOption = entityTypeOptions.find((option) => option.value === newValue.value);
    if (isExistingOption) {
      setCreatingNewCustomDefault(false);
      setSelectedEntityTypeOption(newValue);
      return;
    }

    // this is a new one.
    setCreatingNewCustomDefault(true);
    const option = { label: newValue.label, value: newValue.value };
    setEntityTypeOptions([...entityTypeOptions, option]);
    setSelectedEntityTypeOption(option);
  };

  const handleEntityTypeChange = (newValue) => {
    if (isDirty) {
      setPendingEntityChange(newValue);
      return;
    }
    applyEntityTypeChange(newValue);
  };

  const handleSave = async (e, addToCurrentOverride) => {
    if (e?.preventDefault) e.preventDefault();
    if (saving) return { success: false };
    const shouldAddToCurrent = typeof addToCurrentOverride === "boolean" ? addToCurrentOverride : addToCurrent;

    setSaving(true);
    try {
      const isUpdate = !!currentDefaultsId;
      const result = isUpdate ? await updateCustomDefaults() : await createCustomDefaults();
      if (!result?.success) return result;

      const savedFields = fields;
      setBaselineFields(savedFields);
      setCreatingNewCustomDefault(false);

      // Merge the saved template into availableEntityTypes locally so the dropdown
      // reflects the new/updated entry without a network round trip.
      if (result.defaultTemplate) {
        const savedTemplate = result.defaultTemplate;
        setAvailableEntityTypes((prev) => {
          const idx = prev.findIndex((t) => t.entityType === savedTemplate.entityType);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = savedTemplate;
            return next;
          }
          return [...prev, savedTemplate];
        });
      }

      if (onDefaultsChanged) {
        await onDefaultsChanged();
      }
      setToast(`Default traits for ${entityNoun} saved`);

      if (shouldAddToCurrent) {
        await updateCurrentEntitiesWithFields(savedFields, selectValue?.value);
      }

      // If the user entered this flow from Import, take them back when their save is done.
      if (isCustomizingWhileImporting) {
        setIsCustomizingWhileImporting(false);
        setImportIsOpen(true);
        setIsOpen(false);
      }

      return result;
    } finally {
      setSaving(false);
    }
  };

  const dismissPendingChange = () => setPendingEntityChange(null);

  const discardAndApplyPendingChange = () => {
    const pending = pendingEntityChange;
    setPendingEntityChange(null);
    if (pending !== null) {
      // Effect on selectedEntityTypeOption will reload fields and re-sync baseline.
      applyEntityTypeChange(pending);
    }
  };

  const saveAndApplyPendingChange = async (confirmAddToCurrent) => {
    const pending = pendingEntityChange;
    const result = await handleSave(undefined, confirmAddToCurrent);
    if (!result?.success) return;
    // If the user dismissed the confirm or picked a different target while save was in flight,
    // honor that — the save already happened, but don't auto-navigate to a stale target.
    if (pendingEntityChangeRef.current !== pending) return;
    setPendingEntityChange(null);
    if (pending !== null) {
      applyEntityTypeChange(pending);
    }
  };

  const entityNoun = entityTypeLabel || "element";
  const addToCurrentLabel = `Add to Current ${entityNoun}s`;

  const footer = (
    <CustomFooterStyles>
      <label className="add-to-current-checkbox">
        <input
          type="checkbox"
          checked={addToCurrent}
          onChange={(e) => setAddToCurrent(e.target.checked)}
          disabled={addingDefaultFields}
        />
        <span>{addingDefaultFields ? `Updating ${entityNoun}s...` : addToCurrentLabel}</span>
      </label>
      <button
        className={saving || addingDefaultFields ? "disabled" : ""}
        onClick={handleSave}
        type="button"
        disabled={saving || addingDefaultFields}
      >
        {saving ? "Saving Traits..." : "Save New Defaults"}
      </button>
    </CustomFooterStyles>
  );

  return (
    <GeneralBaseModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={{ main: `Customize ${singleEntityTypeLabel === "Character" ? "Characters" : "Worldbuilding"}`, sub: `Set the default traits your ${entityTypeLabel || "element"}s are created with` }}
      footer={footer}
      onClose={() => {
        if (isCustomizingWhileImporting) {
          setIsCustomizingWhileImporting(false);
          setImportIsOpen(true);
        }
      }}
    >
      <CustomizeEntityDefaultsContainer>
        {(isCharacterMode || !singleEntityType) && (
          <div className="trait-customization-header">
            <div className="role-selector-container">
              Default Traits for:
              <StyledSelect
                className="role-selector"
                ref={selectRef}
                value={selectValue}
                options={entityTypeOptions}
                onChange={handleEntityTypeChange}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                creatable
                placeholder={isCharacterMode ? "Character Type" : "Type"}
                aria-label="Select the element type to customize"
              />
            </div>
            {shouldShowDelete && (
              <TextButton
                className="delete-defaults"
                onClick={() => deleteCustomDefaults({ shouldReset: true })}
              >
                <SVG src="/assets/delete.svg" />
                <p>Delete</p>
              </TextButton>
            )}
            {shouldShowReset && (
              <TextButton
                className="reset-defaults"
                onClick={() => deleteCustomDefaults({ shouldReset: false })}
                src="/assets/restart.svg"
              >
                <p>Reset {readableSelectedLabel}</p>
              </TextButton>
            )}
          </div>
        )}
        <DndContext
          onDragEnd={handleDragEnd}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
        >
          <ul className="trait-list">
            <SortableContext
              items={fields.map((field) => field.id)}
              strategy={verticalListSortingStrategy}
            >
              {fields.map((field) => (
                <SortableItem
                  key={field.id}
                  field={field}
                  onFieldLabelChange={handleChangeFieldLabel}
                  onDeleteField={handleDeleteField}
                />
              ))}
            </SortableContext>
          </ul>
        </DndContext>
        <TextButton
          onClick={handleAddField}
          src="/assets/add.svg"
          className="high-contrast"
          ariaLabel="Add a new default trait"
        >
          Add
        </TextButton>
        <div className="feedback-container">
          {(addingDefaultFields || doneUpdating) &&
            updatedEntityNames.map((entityName, index) => (
              <div
                className="feedback"
                key={`${entityName}-${index}`}
              >
                <SVG src="/assets/check.svg" />
                <p key={entityName}>Updated {entityName}</p>
              </div>
            ))}
        </div>
      </CustomizeEntityDefaultsContainer>
      {pendingEntityChange !== null && (
        <UnsavedChangesConfirm
          entityLabel={entityTypeLabel || "this type"}
          addToCurrentLabel={addToCurrentLabel}
          saving={saving}
          initialAddToCurrent={addToCurrent}
          onSave={saveAndApplyPendingChange}
          onDontSave={discardAndApplyPendingChange}
          onCancel={dismissPendingChange}
        />
      )}
    </GeneralBaseModal>
  );
}
